'use client';

import { Box, Card, Flex, Heading, Stack, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FaCircleArrowRight, FaStopwatch } from 'react-icons/fa6';

import FauconisText from '../utils/fauconisText';

import type { Article } from '@lib/types/directus/directusSchema';
import getReadingTimeForArticle from '@lib/utils/readingTime';
import getImageObject from '@services/directus/getImageUrl';
import { Image } from '@ui/image';
import { Link } from '@ui/link';

interface Props {
  article: Article;
  isHomepage?: boolean;
  mobile?: boolean;
}

export default function CardArticle({ article, isHomepage, mobile }: Props) {
  let finalDescription = null;
  if (article.description) {
    finalDescription =
      article.description.length > 80 ? `${article.description.substring(0, 80)} ...` : article.description;
  }

  const readingTime = getReadingTimeForArticle(article);

  return (
    <Card.Root
      key={article.id}
      backgroundColor="white"
      borderRadius="l4"
      maxWidth="md"
      boxShadow={mobile ? 'sm' : '0px 4px 25px rgba(0, 0, 0, 0.12)'}
      h="full"
      m={mobile ? 1 : 0}
    >
      <Card.Body padding="2" paddingBottom="6">
        <Stack gap="4" h="full">
          <Box position="relative" aspectRatio="16 / 10">
            {article.main_image && (
              <NextLink href={`/guide-mutuelles/article/${article.slug}`}>
                <Image
                  src={getImageObject(article.main_image, 'articleHeader').url}
                  alt={article.title ?? "Image d'illustration"}
                  fill
                  borderTopRadius="l2"
                />
              </NextLink>
            )}
          </Box>
          <Stack paddingX="3" h="full">
            <Flex alignItems="center" justifyContent="space-between" gap="sm" fontSize="sm">
              {typeof article.category === 'object' && (
                <Flex
                  align="center"
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="primary"
                  width="fit-content"
                  maxW={{ base: '240px', md: '250px' }}
                >
                  <NextLink href={`/guide-mutuelles/categorie/${article.category?.slug}`}>
                    <Text
                      fontSize={{ base: 'xs', md: 'sm' }}
                      color="cream"
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      display="block"
                      maxW="200px"
                      title={article.category?.name}
                      textDecoration="none"
                      _hover={{ textDecoration: 'none' }}
                    >
                      {article.category?.name}
                    </Text>
                  </NextLink>
                </Flex>
              )}

              <Flex alignItems="center" gap="1" color="primary.dark">
                <FaStopwatch /> {readingTime} min
              </Flex>
            </Flex>

            {article.title && (
              <Box flex={1} mb={1}>
                <NextLink href={`/guide-mutuelles/article/${article.slug}`}>
                  <Heading
                    as={isHomepage ? 'h3' : 'h2'}
                    fontSize="lg"
                    fontWeight="bold"
                    lineClamp={2}
                    textWrap="balance"
                  >
                    {article.title}
                  </Heading>
                </NextLink>
              </Box>
            )}

            <Stack gap={4}>
              {finalDescription !== null && (
                <Text flex="1" fontSize="lg" textWrap="balance">
                  {finalDescription}
                </Text>
              )}

              <Flex alignItems="flex-end" justifyContent="space-between" gap="6">
                {article.author?.avatar && (
                  <NextLink href={article.author.link ?? '#'} target="_blank">
                    <Flex alignItems="center" gap={3}>
                      <Box position="relative" aspectRatio="1/1" h={8} bg="gray.200" borderRadius="full">
                        <Image
                          src={getImageObject(article.author.avatar).url}
                          alt="Photo de l'auteur"
                          fill
                          borderRadius="full"
                        />
                      </Box>

                      <Stack gap={-4} color="primary.dark">
                        <FauconisText fontSize="md">{`${article.author.first_name} ${article.author.last_name}`}</FauconisText>
                        {article.date && (
                          <FauconisText fontSize="sm">
                            {Intl.DateTimeFormat('fr-FR').format(new Date(article.date))}
                          </FauconisText>
                        )}
                      </Stack>
                    </Flex>
                  </NextLink>
                )}

                <Link href={`/guide-mutuelles/article/${article.slug}`} mr="2" fontSize="lg">
                  Lire <FaCircleArrowRight />
                </Link>
              </Flex>
            </Stack>
          </Stack>
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}
